import { HDate, HebrewCalendar } from '@hebcal/core';

/**
 * Compute seasonal customs.
 *
 * @param {HDate} hdate
 * @param {boolean} isIsrael
 * @param {object} calData - from calendarProvider
 * @returns {object}
 */
export function computeSeasonalCustoms(hdate, isIsrael, calData) {
  const m = hdate.getMonth();
  const d = hdate.getDate();
  const year = hdate.getFullYear();

  // ═══ LeDavid HaShem Ori ═══
  // 1 Elul → Shmini Atzeret (IL) / Simchat Torah (diaspora)
  const ledavidEnd = isIsrael ? 22 : 23; // Tishrei
  const ledavidHashemOri = (m === 6 && d >= 1) || // Elul
    (m === 7 && d <= ledavidEnd); // Tishrei through Shmini Atzeret / Simchat Torah

  // ═══ Pirkei Avot ═══
  // Shabbat after Pesach → Shabbat before RH, chapters 1-6 cycled
  const { pirkeiAvot, pirkeiAvotChapter } = computePirkeiAvot(hdate, isIsrael, calData);

  // ═══ Barchi Nafshi ═══
  // Shabbat Bereshit → Shabbat HaGadol (on Shabbat only)
  const barchiNafshi = computeBarchiNafshi(hdate, calData);

  // ═══ Omer ═══
  const omerDay = calData.omerDay;
  const isOmerSeason = omerDay !== null;

  // ═══ Eruv Tavshilin ═══
  const eruvTavshilin = checkEruvTavshilin(hdate, isIsrael);

  return {
    ledavidHashemOri,
    pirkeiAvot,
    pirkeiAvotChapter,
    barchiNafshi,
    omerDay,
    isOmerSeason,
    eruvTavshilin,
  };
}

/**
 * Pirkei Avot: Shabbat after Pesach → Shabbat before Rosh Hashanah.
 * Chapters 1-6 cycled.
 */
function computePirkeiAvot(hdate, isIsrael, calData) {
  if (!calData.isShabbat) return { pirkeiAvot: false, pirkeiAvotChapter: null };

  const m = hdate.getMonth();
  const d = hdate.getDate();
  const year = hdate.getFullYear();

  // Start: first Shabbat after end of Pesach
  const pesachEnd = isIsrael ? 21 : 22; // Nisan
  const startDate = new HDate(pesachEnd, 1, year).onOrAfter(6); // First Shabbat after Pesach

  // End: Shabbat before Rosh Hashanah (29 Elul or earlier)
  const rh = new HDate(1, 7, year + 1); // Next RH — but this could be same year
  // Actually RH is 1 Tishrei of the SAME year object but next civil year
  // Let's compute properly
  const endDate = new HDate(29, 6, year); // 29 Elul — last possible day
  const lastShabbat = endDate.onOrBefore(6);

  if (hdate.abs() < startDate.abs() || hdate.abs() > lastShabbat.abs()) {
    return { pirkeiAvot: false, pirkeiAvotChapter: null };
  }

  // Skip Chol HaMoed / Yom Tov Shabbatot
  if (calData.isCholHamoed || calData.isYomTov) {
    return { pirkeiAvot: false, pirkeiAvotChapter: null };
  }

  // Count which Shabbat this is since start
  const weeksSinceStart = Math.floor((hdate.abs() - startDate.abs()) / 7);
  const chapter = (weeksSinceStart % 6) + 1;

  return { pirkeiAvot: true, pirkeiAvotChapter: chapter };
}

/**
 * Barchi Nafshi: Shabbat Bereshit → Shabbat HaGadol.
 * Recited on Shabbat afternoons from the first Shabbat after Simchat Torah
 * through the last Shabbat before Pesach (Shabbat HaGadol).
 *
 * Active months: Cheshvan (8) through Nisan before Pesach.
 * Months: 8 (Cheshvan), 9 (Kislev), 10 (Tevet), 11 (Shevat), 12 (Adar),
 *         13 (Adar II in leap year), 1 (Nisan through 14).
 * Also: late Tishrei (7) after Simchat Torah — from 23 Tishrei (IL) / 24 Tishrei onward.
 */
function computeBarchiNafshi(hdate, calData) {
  if (!calData.isShabbat) return false;

  const m = hdate.getMonth();
  const d = hdate.getDate();

  // Late Tishrei (after Simchat Torah) — starting from ~23 Tishrei
  if (m === 7 && d >= 23) return true;

  // Cheshvan through Adar (including Adar II in leap year)
  if (m >= 8 && m <= 13) return true;

  // Nisan — only before Pesach (through 14 Nisan)
  if (m === 1 && d <= 14) return true;

  return false;
}

/**
 * Eruv Tavshilin check — is cooking needed for Shabbat?
 * When Yom Tov falls on Friday (or Thursday-Friday in diaspora).
 */
function checkEruvTavshilin(hdate, isIsrael) {
  const tomorrow = new HDate(hdate.abs() + 1);
  const dayAfter = new HDate(hdate.abs() + 2);

  // Check if tomorrow is YT and followed by Shabbat
  const tomorrowEvents = HebrewCalendar.calendar({
    start: tomorrow,
    end: tomorrow,
    il: isIsrael,
  });

  const isTomorrowYT = tomorrowEvents.some(e => e.getFlags() & 1); // CHAG flag
  if (!isTomorrowYT) return false;

  // YT on Friday → eruv tavshilin needed today (Thursday = erev YT)
  if (tomorrow.getDay() === 5) return true; // Friday is YT, Shabbat follows

  // YT on Thursday + Friday (diaspora 2-day YT ending on Friday)
  if (!isIsrael && tomorrow.getDay() === 4) {
    const dayAfterEvents = HebrewCalendar.calendar({
      start: dayAfter,
      end: dayAfter,
      il: isIsrael,
    });
    const isDayAfterYT = dayAfterEvents.some(e => e.getFlags() & 1);
    if (isDayAfterYT && dayAfter.getDay() === 5) return true;
  }

  return false;
}
